"use client";

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { formatNumberForDisplay, formatInputWithSeparators, handleNumericInputChange } from "../../utils/numeros.js";

const ModalEditarPrecioCliente = ({ open, handleClose, precioCliente, onGuardar, localeConfig }) => {
  const [nuevoMonto, setNuevoMonto] = useState("");
  const [tipoAjuste, setTipoAjuste] = useState("valor");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const locale = localeConfig?.locale ?? "es-AR";

  useEffect(() => {
    if (precioCliente && open) {
      setNuevoMonto(precioCliente.monto ?? "");
      setTipoAjuste("valor");
      setError(null);
    }
  }, [precioCliente, open]);

  const handleGuardar = async () => {
    const valor = (nuevoMonto === "" || nuevoMonto == null) ? NaN : (typeof nuevoMonto === "number" ? nuevoMonto : parseFloat(String(nuevoMonto)));
    if (isNaN(valor) || valor < 0) {
      setError("Ingrese un valor numérico válido.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onGuardar(precioCliente.idItem, precioCliente.idEntidad, precioCliente.idUbicacion, valor, tipoAjuste);
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Ocurrió un error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (!precioCliente) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid", borderColor: "divider", pb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>
          Editar precio - {precioCliente.descripcionEntidad || "Cliente"}
        </Typography>
        <Button size="small" onClick={handleClose} sx={{ minWidth: 0, p: 0.5 }}>
          <CloseIcon />
        </Button>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Cliente"
            value={precioCliente.descripcionEntidad || ""}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Ubicación"
            value={precioCliente.ubicacion || ""}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Precio actual"
            value={precioCliente.monto ?? ""}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              readOnly: true,
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Tipo de ajuste</InputLabel>
            <Select
              value={tipoAjuste}
              label="Tipo de ajuste"
              onChange={(e) => setTipoAjuste(e.target.value)}
            >
              <MenuItem value="valor">Valor fijo ($)</MenuItem>
              <MenuItem value="porcentaje">Porcentaje (%)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={tipoAjuste === "valor" ? "Nuevo precio ($)" : "Porcentaje (%)"}
            value={formatInputWithSeparators(nuevoMonto, locale)}
            onChange={(e) => handleNumericInputChange(e, (val) => setNuevoMonto(val), locale)}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: tipoAjuste === "valor" ? <InputAdornment position="start">$</InputAdornment> : <InputAdornment position="start">%</InputAdornment>,
            }}
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small">
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          variant="contained"
          size="small"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
          sx={{ bgcolor: "#5b5b5f", "&:hover": { bgcolor: "#4a4a4e" } }}
        >
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEditarPrecioCliente;
